import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchDashboardData, fetchDashboardFields } from '../utils/dashboardApi.js'
import { usePollingController } from './usePollingController.js'

/**
 * Hook for managing real-time data updates with automatic retry logic
 * Now uses centralized polling controller and combined API endpoint
 */
export const useRealTimeData = () => {
  const pollingController = usePollingController()
  
  const [state, setState] = useState({
    data: [],
    faults: [],
    availableFields: [],
    loading: true,
    error: null,
    isConnected: false,
  })
  
  const intervalRef = useRef(null)
  const mountedRef = useRef(true)
  const retryCountRef = useRef(0)
  const maxRetries = 3
  
  const availableFieldsRef = useRef([])
  
  // Update ref when state changes
  useEffect(() => {
    availableFieldsRef.current = state.availableFields
  }, [state.availableFields])
  
  // Combined data and fields fetching with simplified retry logic
  const fetchDataAndFields = useCallback(async () => {
    // Only fetch if polling controller allows it
    if (!pollingController.shouldPoll) {
      return
    }
    
    try {
      const [dashboardData, fields] = await Promise.all([
        fetchDashboardData(),
        fetchDashboardFields().catch(() => availableFieldsRef.current), // Use ref to avoid dependency cycle
      ])
      
      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          data: dashboardData.realTimeData,
          faults: dashboardData.faults,
          availableFields: Array.isArray(fields) ? fields : prev.availableFields,
          error: null,
          isConnected: true,
          loading: false,
        }))
        retryCountRef.current = 0
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
      
      if (mountedRef.current) {
        retryCountRef.current += 1
        
        const errorMessage = err.code === 'TIMEOUT' 
          ? 'Connection timeout - retrying...'
          : err.message.includes('Network') || err.message.includes('fetch')
          ? 'Network connection lost - retrying...'
          : err.message
        
        setState(prev => ({
          ...prev,
          error: errorMessage,
          isConnected: false,
          loading: prev.loading && retryCountRef.current === 1 ? false : prev.loading,
        }))
        
        // Exponential backoff retry logic
        if (retryCountRef.current <= maxRetries) {
          const retryDelay = 1000 * 2**(retryCountRef.current - 1)
          setTimeout(fetchDataAndFields, retryDelay)
        } else {
          setState(prev => ({
            ...prev,
            error: 'Connection failed after multiple attempts. Please check your network connection.',
          }))
        }
      }
    }
  }, [maxRetries, pollingController.shouldPoll]) // Added pollingController.shouldPoll dependency
  
  // Simplified update management
  const startUpdates = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    
    fetchDataAndFields()
    intervalRef.current = setInterval(fetchDataAndFields, 1000)
  }, [fetchDataAndFields])
    
  const stopUpdates = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])
  
  const retryConnection = useCallback(() => {
    retryCountRef.current = 0
    setState(prev => ({ ...prev, error: null, loading: true }))
    startUpdates()
  }, [startUpdates])

  // Initialize on mount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopUpdates()
    }
  }, [stopUpdates])
  
  const refresh = useCallback(() => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    fetchDataAndFields()
  }, [fetchDataAndFields])

  return {
    data: state.data,
    faults: state.faults,
    availableFields: state.availableFields,
    loading: state.loading,
    error: state.error,
    isConnected: state.isConnected,
    refresh,
    retryConnection,
    startUpdates,
    stopUpdates,
    pollingController, // Expose polling controller for dashboard
  }
}
